import React, { useState, useEffect } from 'react';
import {
  Card, Button, Modal, Form, Input, Select, Tag, Space,
  Typography, Popconfirm, Row, Col, Statistic,
  Badge, Alert, Empty, Tooltip, List, Avatar,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, InboxOutlined,
  BellOutlined, WarningOutlined, CalendarOutlined, TeamOutlined,
  SoundOutlined, DollarOutlined, ToolOutlined, FileDoneOutlined,
  ExclamationCircleOutlined, CheckCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useCondoStore } from '../store/condoStore';
import { useAuthStore } from '../store/authStore';
import type { Announcement, AnnouncementCategory, AnnouncementPriority, AnnouncementStatus } from '../types';
import { staticApi } from '../utils/staticApi';
import { fromNowServer } from '../utils/relativeTime';

dayjs.extend(relativeTime);

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CATEGORY_CONFIG: Record<AnnouncementCategory, { color: string; icon: React.ReactNode; label: string }> = {
  GENERAL:     { color: 'blue',    icon: <BellOutlined />,              label: 'General' },
  MAINTENANCE: { color: 'orange',  icon: <ToolOutlined />,              label: 'Maintenance' },
  FINANCIAL:   { color: 'green',   icon: <DollarOutlined />,            label: 'Financial' },
  EMERGENCY:   { color: 'red',     icon: <ExclamationCircleOutlined />, label: 'Emergency' },
  EVENT:       { color: 'purple',  icon: <CalendarOutlined />,          label: 'Event' },
  POLICY:      { color: 'cyan',    icon: <FileDoneOutlined />,          label: 'Policy' },
};

const PRIORITY_CONFIG: Record<AnnouncementPriority, { badge: 'success' | 'processing' | 'warning' | 'error'; label: string }> = {
  LOW:    { badge: 'success',    label: 'Low' },
  NORMAL: { badge: 'processing', label: 'Normal' },
  HIGH:   { badge: 'warning',    label: 'High' },
  URGENT: { badge: 'error',      label: 'Urgent' },
};

const STATUS_COLORS: Record<AnnouncementStatus, string> = {
  DRAFT: 'default', PUBLISHED: 'success', ARCHIVED: 'default',
};

const Announcements: React.FC = () => {
  // Sprint 36 — finding H8. `currentCondo` and `user` live in
  // `useAuthStore`, not `useCondoStore`. The original destructure
  // tried to pull both from the wrong store, got `undefined`, and
  // the `if (currentCondo?.id) fetchAnnouncements(...)` guard in
  // the mount effect quietly never fired. Every owner (and admin)
  // saw "No announcements at this time" no matter how many rows
  // the API was ready to return.
  const {
    announcements, fetchAnnouncements, createAnnouncement,
    updateAnnouncement, archiveAnnouncement, deleteAnnouncement,
    isLoading,
  } = useCondoStore() as any;
  const { currentCondo, user } = useAuthStore();

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin_condo';
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAnn, setEditingAnn] = useState<Announcement | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [form] = Form.useForm();

  useEffect(() => {
    if (currentCondo?.id) fetchAnnouncements(currentCondo.id, !isAdmin);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetchAnnouncements is a stable zustand action
  }, [currentCondo?.id, isAdmin]);

  const visible = announcements.filter((a: Announcement) => {
    if (!isAdmin && a.status === 'ARCHIVED') return false;
    // Non-admins shouldn't see expired announcements; admins/managers keep
    // them visible for management (the cards already fade expired rows).
    if (!isAdmin && a.expiresAt && dayjs(a.expiresAt).isBefore(dayjs())) return false;
    if (categoryFilter !== 'all' && a.category !== categoryFilter) return false;
    if (priorityFilter !== 'all' && a.priority !== priorityFilter) return false;
    return true;
  });

  const published = announcements.filter((a: Announcement) => a.status === 'PUBLISHED');
  const urgent = announcements.filter((a: Announcement) => a.priority === 'URGENT' && a.status === 'PUBLISHED');
  // Sprint 138 — R12-12: "Expiring Soon" needs a lower bound. Without one, an
  // ALREADY-EXPIRED notice (expiresAt in the past) still passes `< now+7d` and is
  // counted as expiring soon — a notice that lapsed 34 days ago showed here. Only
  // notices whose expiry is still ahead of us (now ≤ expiresAt < now+7d) qualify.
  const expiringSoon = announcements.filter((a: Announcement) => {
    if (!a.expiresAt || a.status !== 'PUBLISHED') return false;
    const expiry = dayjs(a.expiresAt);
    return !expiry.isBefore(dayjs()) && expiry.isBefore(dayjs().add(7, 'day'));
  });

  const handleOpenCreate = () => {
    setEditingAnn(null);
    form.resetFields();
    form.setFieldsValue({
      category: 'GENERAL', priority: 'NORMAL', targetAudience: 'ALL', status: 'PUBLISHED',
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (a: Announcement) => {
    setEditingAnn(a);
    form.setFieldsValue({
      ...a,
      expiresAt: a.expiresAt || '',
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        condoId: currentCondo?.id || '',
        // Sprint 36 — the previous `user.username` fallback came from
        // when `user` was typed `any` via the wrong store. The real
        // User shape (authStore) has only `name` and `email`.
        createdBy: user?.name || user?.email || 'Admin',
        expiresAt: values.expiresAt || undefined,
      };
      if (editingAnn) {
        await updateAnnouncement(editingAnn.id, payload);
        staticApi.message.success('Announcement updated');
      } else {
        await createAnnouncement(payload);
        staticApi.message.success('Announcement published');
      }
      setModalOpen(false);
    } catch (e) {
      // Client-side AntD validation rejections carry `errorFields` and already
      // render inline under each input — stay silent for those. A backend
      // failure (no errorFields) gets the specific RFC-7807 message surfaced
      // by apiJson (Sprint 117), generic only as a fallback; modal stays open.
      const err = e as { errorFields?: unknown; message?: string };
      if (!err?.errorFields) {
        staticApi.message.error(err?.message || 'Failed to save announcement. Please try again.');
      }
    }
  };

  const handleArchive = async (id: string) => {
    await archiveAnnouncement(id);
    staticApi.message.success('Announcement archived');
  };

  const handleDelete = async (id: string) => {
    await deleteAnnouncement(id);
    staticApi.message.success('Announcement deleted');
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <SoundOutlined style={{ marginRight: 10, color: '#1890ff' }} />
            Announcements & Notice Board
          </Title>
          <Text type="secondary">
            {isAdmin ? 'Create and manage community announcements' : 'Stay informed with the latest updates from management'}
          </Text>
        </div>
        {isAdmin && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
            Post Announcement
          </Button>
        )}
      </div>

      {/* KPI Cards (admin only) */}
      {isAdmin && (
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic title="Published" value={published.length} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic title="Urgent Notices" value={urgent.length} prefix={<ExclamationCircleOutlined />} valueStyle={{ color: urgent.length > 0 ? '#ff4d4f' : undefined }} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic title="Expiring Soon" value={expiringSoon.length} prefix={<CalendarOutlined />} valueStyle={{ color: expiringSoon.length > 0 ? '#faad14' : undefined }} />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card>
              <Statistic title="Total Posted" value={announcements.length} prefix={<BellOutlined />} />
            </Card>
          </Col>
        </Row>
      )}

      {/* Urgent alert banner */}
      {urgent.length > 0 && (
        <Alert
          type="error"
          showIcon
          icon={<WarningOutlined />}
          message={`${urgent.length} Urgent Notice${urgent.length > 1 ? 's' : ''}`}
          description={urgent.map((a: Announcement) => a.title).join(' • ')}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Filters */}
      <Space style={{ marginBottom: 16 }} wrap>
        <Select value={categoryFilter} onChange={setCategoryFilter} style={{ width: 150 }}>
          <Option value="all">All Categories</Option>
          {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
            <Option key={k} value={k}>{v.label}</Option>
          ))}
        </Select>
        <Select value={priorityFilter} onChange={setPriorityFilter} style={{ width: 130 }}>
          <Option value="all">All Priorities</Option>
          <Option value="URGENT">Urgent</Option>
          <Option value="HIGH">High</Option>
          <Option value="NORMAL">Normal</Option>
          <Option value="LOW">Low</Option>
        </Select>
      </Space>

      {/* Announcements List */}
      {visible.length === 0 ? (
        <Card>
          <Empty
            image={<BellOutlined style={{ fontSize: 64, opacity: 0.2 }} />}
            imageStyle={{ height: 80 }}
            description={
              <span>
                {isAdmin ? 'No announcements yet. Post one to inform residents.' : 'No announcements at this time.'}
              </span>
            }
          >
            {isAdmin && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
                Post First Announcement
              </Button>
            )}
          </Empty>
        </Card>
      ) : (
        <List
          dataSource={visible}
          loading={isLoading}
          renderItem={(a: Announcement) => {
            const catCfg = CATEGORY_CONFIG[a.category] || CATEGORY_CONFIG.GENERAL;
            const priCfg = PRIORITY_CONFIG[a.priority] || PRIORITY_CONFIG.NORMAL;
            const isExpired = a.expiresAt && dayjs(a.expiresAt).isBefore(dayjs());
            const isArchived = a.status === 'ARCHIVED';

            return (
              <Card
                key={a.id}
                style={{
                  marginBottom: 12,
                  opacity: isArchived || isExpired ? 0.6 : 1,
                  borderLeft: `4px solid ${a.priority === 'URGENT' ? '#ff4d4f' : a.priority === 'HIGH' ? '#faad14' : '#1890ff'}`,
                }}
                bodyStyle={{ padding: '16px 20px' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Space align="start" size={12}>
                    <Avatar
                      icon={catCfg.icon}
                      style={{ background: '#f0f2f5', color: '#595959' }}
                    />
                    <div>
                      <Space wrap>
                        <Text strong style={{ fontSize: 15 }}>{a.title}</Text>
                        <Tag color={catCfg.color}>{catCfg.label}</Tag>
                        <Badge status={priCfg.badge} text={priCfg.label} />
                        {isAdmin && (
                          <Tag color={STATUS_COLORS[a.status]}>{a.status}</Tag>
                        )}
                        {a.targetAudience !== 'ALL' && (
                          <Tag icon={<TeamOutlined />}>{a.targetAudience}</Tag>
                        )}
                      </Space>
                      <Paragraph
                        style={{ margin: '8px 0 4px', color: '#595959' }}
                        ellipsis={{ rows: 3, expandable: true, symbol: 'more' }}
                      >
                        {a.content}
                      </Paragraph>
                      <Space size={16}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          <CalendarOutlined style={{ marginRight: 4 }} />
                          {fromNowServer(a.createdAt)}
                          {a.createdBy && ` · by ${a.createdBy}`}
                        </Text>
                        {a.expiresAt && (
                          <Text type={isExpired ? 'danger' : 'secondary'} style={{ fontSize: 12 }}>
                            Expires {dayjs(a.expiresAt).format('MMM D, YYYY')}
                          </Text>
                        )}
                      </Space>
                    </div>
                  </Space>
                  {isAdmin && (
                    <Space>
                      <Tooltip title="Edit">
                        <Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEdit(a)} />
                      </Tooltip>
                      {a.status === 'PUBLISHED' && (
                        <Tooltip title="Archive">
                          <Button size="small" icon={<InboxOutlined />} onClick={() => handleArchive(a.id)} />
                        </Tooltip>
                      )}
                      <Popconfirm
                        title="Delete this announcement?"
                        onConfirm={() => handleDelete(a.id)}
                        okText="Delete"
                        okButtonProps={{ danger: true }}
                      >
                        <Tooltip title="Delete">
                          <Button size="small" danger icon={<DeleteOutlined />} />
                        </Tooltip>
                      </Popconfirm>
                    </Space>
                  )}
                </div>
              </Card>
            );
          }}
        />
      )}

      {/* Create / Edit Modal */}
      <Modal
        title={editingAnn ? 'Edit Announcement' : 'Post Announcement'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText={editingAnn ? 'Save Changes' : 'Publish'}
        width={640}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input placeholder="Water Shutoff Notice — Building B" />
          </Form.Item>
          <Form.Item name="content" label="Message" rules={[{ required: true }]}>
            <TextArea rows={5} placeholder="Write your announcement here..." showCount maxLength={2000} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="category" label="Category" rules={[{ required: true }]}>
                <Select>
                  {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
                    <Option key={k} value={k}>{v.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="priority" label="Priority">
                <Select>
                  <Option value="LOW">Low</Option>
                  <Option value="NORMAL">Normal</Option>
                  <Option value="HIGH">High</Option>
                  <Option value="URGENT">Urgent</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="targetAudience" label="Audience">
                <Select>
                  <Option value="ALL">All Residents</Option>
                  <Option value="OWNERS">Owners Only</Option>
                  <Option value="BOARD">Board Only</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select>
                  <Option value="PUBLISHED">Published</Option>
                  <Option value="DRAFT">Draft</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="expiresAt" label="Expiry Date (optional)">
                <Input placeholder="YYYY-MM-DD" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default Announcements;
